import { ExternalPageHeader } from '@/components/layout/external-page-header'
import { ForcePasswordResetForm } from '@/features/authentication/components/force-password-reset-form'
import { useAuth } from '@/features/authentication/components/auth-provider'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

export const Route = createFileRoute('/force-password-reset')({
  beforeLoad: ({ context }) => {
    // Require authentication; otherwise redirect to login.
    if (!context.auth.isAuthenticated && !context.auth.isLoading) {
      const search: { redirect?: string } = {}
      if (typeof window !== 'undefined') {
        const pendingRedirect = window.sessionStorage.getItem('post_force_reset_redirect')
        if (pendingRedirect?.startsWith('/')) {
          search.redirect = pendingRedirect
        }
      }
      throw redirect({ to: '/login', search })
    }
  },
  component: ForcePasswordResetPage,
})

function ForcePasswordResetPage() {
  const navigate = useNavigate()
  const auth = useAuth()

  useEffect(() => {
    // If for some reason the flag was cleared, send user back to dashboard.
    const forceFlag = typeof window !== 'undefined' ? window.localStorage.getItem('force_password_reset') : null
    if (!forceFlag && auth.isAuthenticated) {
      navigate({ to: '/dashboard', replace: true })
    }
  }, [auth.isAuthenticated, navigate])

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 dark:from-slate-900 dark:via-emerald-900/20 dark:to-teal-900/20">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-teal-500/20 blur-3xl" />
        <div className="absolute left-1/4 top-1/3 h-64 w-64 rounded-full bg-cyan-500/10 blur-3xl" />
      </div>

      <ExternalPageHeader className="relative z-10 border-emerald-100/60 bg-white/80 dark:border-slate-800/60 dark:bg-slate-950/80" />

      <main className="relative z-10 flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-xl space-y-6">
          <div className="text-center">
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
              Security check required
            </p>
            <h1 className="mt-1 text-3xl font-semibold text-slate-900 dark:text-slate-50">
              Change your password to continue
            </h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Your account requires a password update. This page must be completed before you can access the portal.
            </p>
          </div>
          <ForcePasswordResetForm />
          <p className="text-center text-xs text-slate-600 dark:text-slate-300">
            Need help? Contact support at <a className="font-semibold underline" href="mailto:support@uibfs.or.ug">support@uibfs.or.ug</a>
          </p>
        </div>
      </main>
    </div>
  )
}
